import { IsoDate } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { Maybe, Nullable } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { plural } from "@figurepos/fgr-lib-business-logic/lib/formatters/plural"
import { DateValue, parseDate } from "@internationalized/date"
import { RangeValue } from "@react-types/shared"
import {
    addDays,
    differenceInDays,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    isValid,
    parse,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
} from "date-fns"
import { WeekStartDay } from "../../screens/settings/types"
import { CustomIcon } from "../Icons/CustomIcons"
import {
    DateRangeFormattedValue,
    DateRangePickerError,
    DateRangePickerValue,
    PredefinedDateGroup,
    PredefinedDateGroupId,
} from "./types"

export const ISO_DATE_FORMAT = "yyyy-MM-dd"
export const FORMATTED_DATE_FORMAT = "MM/dd/yyyy"

export const parseValueForCalendar = (value: {
    start: Nullable<IsoDate>
    end: Nullable<IsoDate>
}): RangeValue<DateValue> | null => {
    try {
        if (!value.start || !value.end) {
            return null
        }
        return { start: parseDate(value.start), end: parseDate(value.end) }
    } catch {
        return null
    }
}

export const getFormattedDateRangeValue = (value: DateRangePickerValue): DateRangeFormattedValue => {
    try {
        const startDate = value?.start ? parse(value.start, ISO_DATE_FORMAT, new Date()) : null
        const startFormatted = startDate ? format(startDate, FORMATTED_DATE_FORMAT) : null

        const endDate = value?.end ? parse(value.end, ISO_DATE_FORMAT, new Date()) : null
        const endFormatted = endDate ? format(endDate, FORMATTED_DATE_FORMAT) : null

        return { start: startFormatted, end: endFormatted }
    } catch {
        return { start: null, end: null }
    }
}

export const getIsoDateNow = (): IsoDate => format(new Date(), ISO_DATE_FORMAT)

export const parseDateFromInput = (value: string, weekStartDay: WeekStartDay): Nullable<Date> => {
    const supportedFormats = ["M/d", "MM/dd", "M/d/yy", "M/d/yyyy", "MM/dd/yyyy", "MMdd", "MMddyyyy"]

    for (const format of supportedFormats) {
        try {
            const date = parse(value, format, new Date(), { weekStartsOn: weekStartDayToNumber(weekStartDay) })
            if (isValid(date)) {
                return date
            }
        } catch {
            // Ignore errors and try the next format
        }
    }

    return null
}

export const isDateRangeValid = (start: IsoDate | null, end: IsoDate | null): boolean => {
    if (!start || !end) {
        return true
    }
    const startDate = parse(start, ISO_DATE_FORMAT, new Date())
    const endDate = parse(end, ISO_DATE_FORMAT, new Date())

    if (startDate > endDate || endDate < startDate) {
        return false
    }
    return true
}

export const getInputDaysLabel = (
    value: DateRangePickerValue,
    error: DateRangePickerError | null
): Nullable<string> => {
    if (!value.start || !value.end || error?.start || error?.end) {
        return null
    }
    const startDate = parse(value.start, ISO_DATE_FORMAT, new Date())
    const startDay = format(startDate, "EEEE") // eg. from Monday

    const endDate = parse(value.end, ISO_DATE_FORMAT, new Date())
    const endDay = format(endDate, "EEEE") // eg. to Friday

    const daysCount = differenceInDays(endDate, startDate) + 1

    return `${startDay} - ${endDay} (${daysCount} ${plural("days", daysCount)})`
}

export const getPredefinedDateGroups = (
    today: IsoDate,
    maxDaysRange: Maybe<number>,
    weekStartDay: WeekStartDay
): PredefinedDateGroup[] => {
    const todayDate = parse(today, ISO_DATE_FORMAT, new Date())
    const yesterday = subDays(todayDate, 1)

    const thisWeekStart = startOfWeek(todayDate, { weekStartsOn: weekStartDayToNumber(weekStartDay) })
    const thisWeekEnd = todayDate

    const lastWeekStart = subDays(thisWeekStart, 7)
    const lastWeekEnd = endOfWeek(lastWeekStart, { weekStartsOn: weekStartDayToNumber(weekStartDay) })

    const thisMonthStart = startOfMonth(todayDate)
    const thisMonthEnd = todayDate

    const lastMonthStart = startOfMonth(subDays(thisMonthStart, 1))
    const lastMonthEnd = endOfMonth(lastMonthStart)

    const last3MonthsStart = startOfMonth(subMonths(todayDate, 3))
    const last3MonthsEnd = lastMonthEnd

    const last7DaysStart = subDays(todayDate, 6)
    const last7DaysEnd = todayDate

    const last28DaysStart = subDays(todayDate, 27)
    const last28DaysEnd = todayDate

    const last90DaysStart = subDays(todayDate, 89)
    const last90DaysEnd = todayDate

    const last365DaysStart = subDays(todayDate, 364)
    const last365DaysEnd = todayDate

    const yearToDateStart = startOfYear(todayDate)
    const yearToDateEnd = todayDate

    const lastYearStart = startOfYear(subDays(yearToDateStart, 1))
    const lastYearEnd = endOfYear(lastYearStart)

    const iconClassName = "w-[16px] h-auto fill-black/80"

    return [
        {
            groupId: PredefinedDateGroupId.Main,
            groupLabel: "Main",
            dates: [
                {
                    value: { start: today, end: today },
                    label: "Today",
                    icon: <CustomIcon icon="calendarToday" className={iconClassName} />,
                },
                {
                    value: { start: format(yesterday, ISO_DATE_FORMAT), end: format(yesterday, ISO_DATE_FORMAT) },
                    label: "Yesterday",
                    icon: <CustomIcon icon="calendarYesterday" className={iconClassName} />,
                },
            ],
        },
        {
            groupId: PredefinedDateGroupId.Weekly,
            groupLabel: "Weekly",
            dates: [
                {
                    value: { start: format(thisWeekStart, ISO_DATE_FORMAT), end: format(thisWeekEnd, ISO_DATE_FORMAT) },
                    label: "This Week",
                },
                {
                    value: { start: format(lastWeekStart, ISO_DATE_FORMAT), end: format(lastWeekEnd, ISO_DATE_FORMAT) },
                    label: "Last Week",
                },
            ],
        },
        {
            groupId: PredefinedDateGroupId.Monthly,
            groupLabel: "Monthly",
            dates: [
                {
                    value: {
                        start: format(thisMonthStart, ISO_DATE_FORMAT),
                        end: format(thisMonthEnd, ISO_DATE_FORMAT),
                    },
                    label: "This Month",
                },
                {
                    value: {
                        start: format(lastMonthStart, ISO_DATE_FORMAT),
                        end: format(lastMonthEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last Month",
                },
                {
                    value: {
                        start: format(last3MonthsStart, ISO_DATE_FORMAT),
                        end: format(last3MonthsEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last 3 Months",
                },
            ],
        },
        {
            groupId: PredefinedDateGroupId.Rolling,
            groupLabel: "Rolling",
            dates: [
                {
                    value: {
                        start: format(last7DaysStart, ISO_DATE_FORMAT),
                        end: format(last7DaysEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last 7 Days",
                },
                {
                    value: {
                        start: format(last28DaysStart, ISO_DATE_FORMAT),
                        end: format(last28DaysEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last 28 Days",
                },
                {
                    value: {
                        start: format(last90DaysStart, ISO_DATE_FORMAT),
                        end: format(last90DaysEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last 90 Days",
                },
                {
                    value: {
                        start: format(last365DaysStart, ISO_DATE_FORMAT),
                        end: format(last365DaysEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last 365 Days",
                },
                {
                    value: {
                        start: format(lastYearStart, ISO_DATE_FORMAT),
                        end: format(lastYearEnd, ISO_DATE_FORMAT),
                    },
                    label: "Last Year",
                },
                {
                    value: {
                        start: format(yearToDateStart, ISO_DATE_FORMAT),
                        end: format(yearToDateEnd, ISO_DATE_FORMAT),
                    },
                    label: "Year to Date",
                },
            ],
        },
    ]
        .map(range => {
            if (!maxDaysRange) {
                return range
            }

            // Filter out ranges that are longer than the maxDaysRange
            const dates = range.dates.filter(date => {
                const startDate = parse(date.value.start, ISO_DATE_FORMAT, new Date())
                const endDate = parse(date.value.end, ISO_DATE_FORMAT, new Date())
                return Math.abs(differenceInDays(endDate, startDate)) <= maxDaysRange
            })

            if (dates.length === 0) {
                return null
            }

            return {
                ...range,
                dates: dates,
            }
        })
        .filter(r => !!r) as PredefinedDateGroup[]
}

export const moveDateRange = (
    direction: "back" | "forward",
    value: DateRangePickerValue,
    minDateIso: IsoDate | null,
    maxDateIso: IsoDate | null
): DateRangePickerValue | null => {
    if (!value.start || !value.end) {
        return null
    }

    // Move 'start' and 'end' dates back/forward by the amount of total selected days
    // e.g. if the selected range is 6 - 9. February, move it to 2 - 5. February
    const startDate = parse(value.start, ISO_DATE_FORMAT, new Date())
    const endDate = parse(value.end, ISO_DATE_FORMAT, new Date())
    const daysCount = Math.abs(differenceInDays(startDate, endDate)) + 1

    let newStartDate = direction === "back" ? subDays(startDate, daysCount) : addDays(startDate, daysCount)
    let newEndDate = direction === "back" ? subDays(endDate, daysCount) : addDays(endDate, daysCount)

    // If min or max date is set, we need to make sure that the selected range is within the limits.
    const minDate = minDateIso ? parse(minDateIso, ISO_DATE_FORMAT, new Date()) : null
    const maxDate = maxDateIso ? parse(maxDateIso, ISO_DATE_FORMAT, new Date()) : null

    // If the range is completely outside the min/max date, return null
    if ((minDate && newEndDate < minDate) || (maxDate && newStartDate > maxDate)) {
        return null
    }

    // If the range is partially outside the min/max date, adjust the range to be within
    if (minDate && newStartDate < minDate) {
        newStartDate = minDate
    }
    if (maxDate && newEndDate > maxDate) {
        newEndDate = maxDate
    }

    return { start: format(newStartDate, ISO_DATE_FORMAT), end: format(newEndDate, ISO_DATE_FORMAT) }
}

export const stripDateInput = (value: string): string => {
    // Allow only numbers and slashes, remove all other characters
    return value.replace(/[^0-9/]/g, "")
}

export const adjustRangeToFitMaxRange = (
    maxRange: Maybe<number>,
    value: DateRangePickerValue
): DateRangePickerValue => {
    if (!value.start || !value.end || !maxRange) {
        return value
    }

    const start = parse(value.start, ISO_DATE_FORMAT, new Date())
    const end = parse(value.end, ISO_DATE_FORMAT, new Date())

    const diff = Math.abs(differenceInDays(end, start))
    // If the current selected range is bigger than the max range, we should adjust the start date
    if (diff > maxRange) {
        const newStart = subDays(end, maxRange - 1)
        return {
            start: format(newStart, ISO_DATE_FORMAT),
            end: value.end,
        }
    }

    return value
}

export const weekStartDayToNumber = (day: WeekStartDay): 0 | 1 | 2 | 3 | 4 | 5 | 6 => {
    const map: Record<WeekStartDay, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
    }
    return map[day]
}

export const weekStartDayToCalendarConfig = (
    day: WeekStartDay
): "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat" => {
    const map: Record<WeekStartDay, "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat"> = {
        Sunday: "sun",
        Monday: "mon",
        Tuesday: "tue",
        Wednesday: "wed",
        Thursday: "thu",
        Friday: "fri",
        Saturday: "sat",
    }
    return map[day]
}
